import { Injectable, signal, WritableSignal } from '@angular/core';
import { Store } from '@ngrx/store';
import { DatePickerEnum, DatePickerType } from 'ngx-date-range-picker';
import { NgxDropdownOption } from 'ngx-dropdown';
import { BehaviorSubject } from 'rxjs';
import { AppState } from '../../store';
import * as Selectors from '../../store/selectors/com.selectors';

export interface AppliedFilters {
  registrationId: string;
  dateRangeStart: string;
  dateRangeEnd: string;
  datePickerType: DatePickerType;
}

@Injectable({
  providedIn: 'root'
})
export class FiltersStateService {
  public isApplyButtonDisabledSignal: WritableSignal<boolean> = signal(false);
  // Current applied filters
  private appliedFiltersSignal: WritableSignal<AppliedFilters | null> = signal(null);

  // Pending filters (not yet applied)
  private pendingFiltersSignal: WritableSignal<AppliedFilters | null> = signal(null);

  // Loading states
  private isLoadingSignal: WritableSignal<boolean> = signal(false);

  // Automatic selection flag
  private isAutomaticSelectionSignal: WritableSignal<boolean> = signal(false);

  // Date picker type
  private datePickerTypeSignal: WritableSignal<DatePickerType> = signal(DatePickerEnum.WeekPicker);

  // Subject for apply button clicks
  private applyClickedSubject = new BehaviorSubject<AppliedFilters | null>(null);
  public applyClicked$ = this.applyClickedSubject.asObservable();

  // Public readonly signals
  public readonly appliedFilters = this.appliedFiltersSignal.asReadonly();
  public readonly pendingFilters = this.pendingFiltersSignal.asReadonly();
  public readonly isLoading = this.isLoadingSignal.asReadonly();
  public readonly isAutomaticSelection = this.isAutomaticSelectionSignal.asReadonly();
  public readonly datePickerType = this.datePickerTypeSignal.asReadonly();
  protected currentRegViewConfig = this.store.selectSignal(Selectors.selectCurrentRegViewConfig);

  constructor(private store: Store<AppState>) {}

  /**
   * Set date picker type (DatePicker for graph view, WeekPicker for table view)
   */
  setDatePickerType(type: DatePickerType): void {
    this.datePickerTypeSignal.set(type);
  }

  /**
   * Set pending filters (user is changing filters but hasn't applied yet)
   */
  setPendingFilters(filters: AppliedFilters): void {
    this.pendingFiltersSignal.set(filters);
  }

  /**
   * Apply the pending filters
   */
  applyFilters(): void {
    const pending = this.pendingFiltersSignal();
    if (!pending) return;

    // Save as applied
    this.appliedFiltersSignal.set(pending);

    // Clear automatic selection flag
    this.isAutomaticSelectionSignal.set(false);
    setTimeout(() => {
      this.isApplyButtonDisabledSignal.set(true);
    }, 0);

    // Emit for subscribers
    this.applyClickedSubject.next(pending);
  }

  /**
   * Mark as automatic selection (program changed, loading registrations)
   */
  setAutomaticSelection(isAutomatic: boolean): void {
    this.isAutomaticSelectionSignal.set(isAutomatic);
  }

  /**
   * Set loading state
   */
  setLoading(loading: boolean): void {
    this.isLoadingSignal.set(loading);
  }

  /**
   *  Set apply button changing state
   */
  setApplyButtonChanging(selectedReg?: NgxDropdownOption | null): void {
    setTimeout(() => {
      this.isApplyButtonDisabledSignal.set(this.currentRegViewConfig()?.registrationId === selectedReg?.id);
    }, 0);
  }
}
